import React, { useMemo } from "react";
import { Grid, Button, Skeleton, CircularProgress, Alert } from "@mui/material";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Router } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { pdfUtils } from "../utils/pdfUtils";
import DeviceBrandDevicesTable from "../components/Tables/DeviceBrandDevicesTable";
import DeviceBrandGraph from "../components/Graphs/DeviceBrandGraph";
import CardInfo from "../components/Cards/CardInfo";
import PageTitle from "../components/Layout/PageTitle";
import { useDevices, useDeviceBrands } from "../hooks/useDevices";

function DeviceBrand() {
  const params = useParams();
  const brandId = parseInt(params.id);

  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useDevices();
  const {
    data: deviceBrands,
    isLoading: brandsLoading,
    error: brandsError,
  } = useDeviceBrands();

  const isLoading = devicesLoading || brandsLoading;
  const hasError = devicesError || brandsError;

  // Memoized calculations
  const { deviceBrand, devicesFiltered, stats } = useMemo(() => {
    if (!devices || !deviceBrands) {
      return { deviceBrand: null, devicesFiltered: [], stats: {} };
    }

    const brand = deviceBrands.find((brand) => brand.id === brandId);
    const filtered = devices.filter(
      (device) => device.device_brand_id === brandId,
    );

    const devicesHeardLast24h = filtered.filter((device) => {
      let time_updated = new Date(device.last_heard);
      let now = new Date();
      return now.getTime() - time_updated.getTime() < 24 * 60 * 60 * 1000;
    }).length;

    const claimedDevices = filtered.filter((device) => device.claimed).length;
    const unclaimedDevices = filtered.filter(
      (device) => !device.claimed,
    ).length;

    return {
      deviceBrand: brand,
      devicesFiltered: filtered,
      stats: {
        total: filtered.length,
        heardLast24h: devicesHeardLast24h,
        claimed: claimedDevices,
        unclaimed: unclaimedDevices,
      },
    };
  }, [devices, deviceBrands, brandId]);

  if (isLoading) {
    return (
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12} textAlign="center">
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (hasError) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Alert severity="error">
            Failed to load device brand data:{" "}
            {(devicesError || brandsError)?.message || "Unknown error"}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const brandName = deviceBrand?.description || "";

  return (
    <Grid container spacing={1}>
      <PageTitle
        title={brandName ? brandName : <Skeleton variant="text" width={200} />}
        showBack
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            pdfUtils.downloadDeviceBrandPDF(params.id, brandName);
          }}
        >
          Download Report
        </Button>
      </PageTitle>
      <Grid item xs={12} md={12}>
        <DeviceBrandGraph dateLength={30} deviceBrand={brandId} />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardInfo
          avatar={<Router fontSize="large" />}
          title="Total Devices"
          value={stats.total || 0}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardInfo
          avatar={<Router fontSize="large" />}
          title="Devices Heard Last 24hr"
          value={stats.heardLast24h || 0}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardInfo
          avatar={<Router fontSize="large" />}
          title="Claimed Devices"
          value={stats.claimed || 0}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <CardInfo
          avatar={<Router fontSize="large" />}
          title="Unclaimed Devices"
          value={stats.unclaimed || 0}
        />
      </Grid>

      <Grid item xs={12} md={12}>
        <DeviceBrandDevicesTable brandID={brandId} devices={devicesFiltered} />
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(DeviceBrand);
